import { generatePath } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { Observable } from 'rxjs';

import {
  ConfirmPaymentRequest,
  FetchCertainPaymentMethodRequest,
  FetchCertainPaymentMethodResponse,
  FetchDirecta24OptionsRequest,
  ProcessPaymentRequest,
  ProcessPaymentResponse,
  RegisterPaymentRequest,
  UploadFileRequest,
} from '@cashier/cashier-api-model';
import { IntegrationSources, PaymentAttribute } from '@cashier/cashier-model';

import { ApiResolver } from './apiResolver';
import { BaseClient } from './client';
import { Api } from './model';
import {
  ContentTypes,
  convertToProcessRequestBody,
  extractFileExtension,
} from './utils';

export interface CashierClientInterface {
  setVersionHeader(version: string): void;

  processPayment(
    request: ProcessPaymentRequest,
  ): Observable<ProcessPaymentResponse>;

  registerPayment(
    request: RegisterPaymentRequest,
  ): Observable<ProcessPaymentResponse>;

  confirmPayment(
    request: ConfirmPaymentRequest,
  ): Observable<ProcessPaymentResponse>;

  fetchPaymentInfo(transactionId: string): Observable<any>;

  fetchCertainPaymentMethod(
    request: FetchCertainPaymentMethodRequest,
  ): Observable<FetchCertainPaymentMethodResponse>;

  fetchDirecta24Options(request: FetchDirecta24OptionsRequest): Observable<any>;

  fetchMifinityOptions(merchantId: string): Observable<any>;

  uploadFile(request: UploadFileRequest): Observable<string>;
}

export class CashierClient
  extends BaseClient
  implements CashierClientInterface
{
  private readonly clientApp = 'cashier';
  private clientVersion: string | null = null;
  private readonly api: Api;

  constructor(
    apiUrl: string,
    private readonly directa24ApiUrl: string,
    private readonly mifinityApiUrl: string,
    private readonly acceptLanguage: string,
    private readonly authorisationToken: string,
    private readonly apiVersions: string, //@TODO: will be unnecessary after PGW-3023 deploy to PROD
    clientVersion?: string,
  ) {
    super(apiUrl);
    this.api = ApiResolver.byVersion(apiVersions); //@TODO: will be unnecessary after PGW-3023 deploy to PROD

    if (clientVersion !== undefined) {
      this.clientVersion = clientVersion;
    }
  }

  setVersionHeader(version: string): void {
    this.clientVersion = version;
  }

  processPayment(
    request: ProcessPaymentRequest,
  ): Observable<ProcessPaymentResponse> {
    const requestUrlMap = {
      [IntegrationSources.Internal]: `${this.api.post.cashierMain}/${request.vendor}/process`,
      [IntegrationSources.External]: this.api.post.externalProcessTransaction,
    };

    return this.post(
      requestUrlMap[request.source],
      convertToProcessRequestBody(request),
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
        'Content-Type': 'application/json',
        'Client-App': this.clientApp,
        ...(this.clientVersion && { 'Client-Version': this.clientVersion }),
      },
    );
  }

  registerPayment({
    referenceId,
    type,
    walletId,
    sessionId,
    merchantId,
    methodId,
    attributes,
  }: RegisterPaymentRequest): Observable<ProcessPaymentResponse> {
    const { accountId, ...attributesWithoutAccountId } = attributes;

    return this.post(
      this.api.post.registerPayment,
      {
        referenceId,
        type,
        walletId,
        sessionId,
        merchantId,
        registration: {
          accountId: accountId || null,
          methodId,
          attributes: attributesWithoutAccountId,
        },
      },
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
        'Content-Type': 'application/json',
        'Client-App': this.clientApp,
        ...(this.clientVersion && { 'Client-Version': this.clientVersion }),
      },
    );
  }

  fetchPaymentInfo(transactionId: string): Observable<PaymentAttribute> {
    return this.get<PaymentAttribute>(
      `${this.api.get.paymentInfo}/${transactionId}`,
      null,
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
      },
    );
  }

  fetchCertainPaymentMethod({
    id,
    type,
    bonus,
  }: FetchCertainPaymentMethodRequest): Observable<FetchCertainPaymentMethodResponse> {
    return this.get<FetchCertainPaymentMethodResponse>(
      `${this.api.get.cashierPaymentMethods}/${type}/${id}`,
      {
        bonusCode: bonus,
      },
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
      },
    );
  }

  confirmPayment({
    transactionId,
    paymentAttributes,
  }: ConfirmPaymentRequest): Observable<ProcessPaymentResponse> {
    return this.put<ProcessPaymentResponse>(
      `${this.api.put.confirmRequest}/${transactionId}`,
      { paymentAttributes },
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
        'Content-Type': 'application/json',
      },
    );
  }

  fetchDirecta24Options(
    parameters: FetchDirecta24OptionsRequest,
  ): Observable<any> {
    return this.postWithoutApiUrl<any>(this.directa24ApiUrl, parameters);
  }

  fetchMifinityOptions(merchantId: string): Observable<any> {
    return this.getWithoutApiUrl<any>(
      generatePath(this.mifinityApiUrl, { merchantId }),
    );
  }

  uploadFile({
    data: { file },
    transactionId,
  }: UploadFileRequest): Observable<string> {
    const contentType = file.type as ContentTypes;

    if (!contentType) {
      Sentry.captureException(new Error('####Unsupported file type####:'), {
        extra: {
          fileType: file.type,
        },
      });
    }

    const documentType = extractFileExtension(contentType);

    return this.post(
      generatePath(this.api.post.uploadFile, {
        transactionId,
        documentType,
      }),
      file,
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
        'Content-Type': file.type,
      },
    );
  }
}
